import { useSyncExternalStore } from "react";
import { getIntlLocale, translate } from "@/i18n/translate";
import type { MonitoringWindow } from "./types";

/** Number / duration / time formatting of the monitoring board, in the UI language. */

const nf = (options?: Intl.NumberFormatOptions) => new Intl.NumberFormat(getIntlLocale(), options);

export const DASH = "–";

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return nf({ maximumFractionDigits: value < 10 && value % 1 !== 0 ? 1 : 0 }).format(value);
}

/** 1234 -> "1.2k" style for axes and tiles */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return nf({ notation: "compact", maximumFractionDigits: 1 }).format(value);
}

/** milliseconds: "4 ms", "350 ms", "1.24 s", "1 min 5 s" */
export function formatMs(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  if (value < 1) return `${nf({ maximumFractionDigits: 2 }).format(value)} ms`;
  if (value < 1000) return `${nf({ maximumFractionDigits: 0 }).format(value)} ms`;
  if (value < 60_000) return `${nf({ maximumFractionDigits: value < 10_000 ? 2 : 1 }).format(value / 1000)} s`;
  return formatDuration(value / 1000);
}

/** short axis ticks of a latency axis: "350", "1.2 s" */
export function formatMsTick(value: number): string {
  if (value >= 1000) return `${nf({ maximumFractionDigits: 1 }).format(value / 1000)} s`;
  return `${nf({ maximumFractionDigits: 0 }).format(value)} ms`;
}

/** seconds: "45 s", "12 min", "3 h 20 min", "2 d 4 h" */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return DASH;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 && m < 10 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return nf({ style: "percent", maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
}

export function formatMb(value: number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return value >= 1024 ? `${nf({ maximumFractionDigits: 2 }).format(value / 1024)} GB`
    : `${nf({ maximumFractionDigits: 0 }).format(value)} MB`;
}

/** "18:03:12" */
export function formatClock(ts: number | null | undefined, seconds = true): string {
  if (!ts) return DASH;
  return new Date(ts).toLocaleTimeString(getIntlLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
  });
}

/** "18. 10. 18:03:12" (date only when not today) */
export function formatStamp(ts: number | null | undefined): string {
  if (!ts) return DASH;
  const date = new Date(ts);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return date.toLocaleString(getIntlLocale(), {
    ...(sameDay ? {} : { day: "numeric", month: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** time axis tick for a chart over `window` */
export function formatAxisTime(ts: number, window: MonitoringWindow): string {
  const date = new Date(ts);
  if (window === "7d") {
    return date.toLocaleString(getIntlLocale(), { weekday: "short", hour: "2-digit" });
  }
  return date.toLocaleTimeString(getIntlLocale(), { hour: "2-digit", minute: "2-digit" });
}

/** tooltip header of a bucket: "Sat 18. 10. 14:00 – 14:15" */
export function formatBucket(ts: number, stepMs: number): string {
  const start = new Date(ts);
  const end = new Date(ts + stepMs);
  const day = start.toLocaleDateString(getIntlLocale(), { weekday: "short", day: "numeric", month: "numeric" });
  const time = (d: Date) => d.toLocaleTimeString(getIntlLocale(), { hour: "2-digit", minute: "2-digit" });
  return `${day} ${time(start)} – ${time(end)}`;
}

/** "12 s ago", "5 min ago", "3 h ago" (in the UI language) */
export function formatAgo(ts: number | null | undefined, now: number = Date.now()): string {
  if (!ts) return DASH;
  const seconds = Math.round((now - ts) / 1000);
  if (seconds < 0) return translate("monitoring.time.justNow");
  if (seconds < 5) return translate("monitoring.time.justNow");
  return translate("monitoring.time.ago", { duration: formatDuration(seconds) });
}

// --- a shared 1 s clock: relative times re-render without re-rendering their parents -------------------
let nowValue = Date.now();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!timer) {
    timer = setInterval(() => {
      nowValue = Date.now();
      listeners.forEach((l) => l());
    }, 1000);
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** the current time, updated once a second */
export function useNow(): number {
  return useSyncExternalStore(subscribe, () => nowValue, () => nowValue);
}

/** sum of a series ignoring nulls */
export const sum = (values: (number | null | undefined)[] | undefined) =>
  (values ?? []).reduce<number>((total, v) => total + (typeof v === "number" ? v : 0), 0);

/** last non-null value of a series */
export const last = <T,>(values: (T | null | undefined)[] | undefined): T | null => {
  if (!values) return null;
  for (let i = values.length - 1; i >= 0; i -= 1) {
    const v = values[i];
    if (v !== null && v !== undefined) return v;
  }
  return null;
};
